import { BusinessError } from '@marketing-milk/interfaces'
import { Button, Chip, Tooltip, Typography } from '@material-ui/core'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import HistoryIcon from '@material-ui/icons/History'
import WarningIcon from '@material-ui/icons/Warning'
import { businessManagerService } from 'app/service/business-manager'
import {
  CommonFBBusinessObjectFields,
  FBAssignableField as FBAssignableFieldType,
  FBAssignAssetResult,
} from 'app/service/business-manager/BusinessManager.types'
import { severityColors } from 'pages/business/accounts/builder/error-center/severity'
import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import BmAvatar from './BmAvatar'
import FacebookObjectSelector, { FBObjectTypeString } from './facebook-object-selector'
import FbFieldChangeDialog from './FbFieldChangeDialog'

// Page has a stable public picture URL at this endpoint (same one FacebookObjectSelector already uses
// for its pre-selected fallback) — Instagram business account IDs are NOT valid against this endpoint,
// so Instagram's own picture can only come from the assign response's `picture` field (see
// ownAssetPicture below), not guessed from the id. Ad Account/Dataset/Offline Dataset have no picture
// of their own at all, so those rows show the owning Business Manager's picture instead (passed in via
// fbBusinessPictureUri, resolved server-side).
const pageOwnPictureUrl = (type: FBObjectTypeString, assetId?: string) =>
  type === 'page' && assetId
    ? `https://graph.facebook.com/${assetId}/picture?type=large`
    : undefined

// Maps the FacebookObjectSelector's type string to the backend's FBAssignableField, since the two
// evolved independently (selector types predate this assign flow).
const FIELD_MAP: Record<FBObjectTypeString, FBAssignableFieldType> = {
  ad_account: 'ad_account',
  page: 'page',
  pixel: 'dataset',
  offline: 'offline_dataset',
  ig: 'instagram',
}

interface FbAssignableFieldProps {
  type: FBObjectTypeString
  title: string
  // Shows an "Optional" label next to the title — for fields that aren't required for the business to
  // be considered fully configured (see validateBusiness's FB-Builder branch), so it's clear at a glance
  // that leaving this unset is fine, without needing a separate enable/disable toggle to convey the same.
  optional?: boolean
  tooltipText?: string
  accountID?: string
  currentAssetId?: string
  currentAssetName?: string | null
  // True while creating a brand-new business (never saved) — every field's confirm dialog is skipped
  // regardless of currentAssetId, since nothing picked so far has any real persisted history to warn
  // about losing until the business has actually been saved at least once.
  isNewBusiness?: boolean
  // Instagram-only: the account's own picture, resolved server-side on business load (see
  // pageOwnPictureUrl below for why this can't be guessed from a public URL the way Page's can).
  initialOwnPictureUri?: string
  fbBusinessId?: string
  fbBusinessName?: string
  fbBusinessPictureUri?: string
  hasError: boolean
  disabled: boolean
  onAssigned: (result: FBAssignAssetResult, assetId: string, assetName: string) => void
  // A warning the parent knows about independently of this field's own last assign-time warning —
  // Dataset/Offline Dataset/Instagram use this when the Ad Account changes out from under them (see
  // recheckAdAccountDependentFields), and Page uses it for a persisted, non-fatal lead_access_error
  // (the connection itself is fine; only lead syncing is affected, so it's a warning, not hasError).
  // Takes priority over this field's own last assign-time warning (localWarning).
  externalWarning?: string
  // Page and Instagram only — the other fields are required for the business to be considered fully
  // configured (see validateBusiness's FB-Builder branch), so they don't get an unassign option.
  clearable?: boolean
  onCleared?: () => void
  // Ad Account/Page only: passed through to FacebookObjectSelector's empty-results prompt and refetch
  // mechanism — see its onRefreshAssets/refreshToken/onRefreshComplete prop comments for why these don't
  // apply to the other asset types.
  onRefreshAssets?: () => void
  refreshingAssets?: boolean
  refreshToken?: number
  onRefreshComplete?: () => void
}

// Shown above the selector/read-only row in both states, whenever we know which Business Manager the
// assigned (or about-to-be-assigned) asset belongs to — the panel should always make that visible, not
// just after a successful assign.
const BmInfoHeader = ({
  id,
  name,
  pictureUri,
}: {
  id?: string
  name?: string
  pictureUri?: string
}) => {
  if (!id && !name && !pictureUri) return null

  return (
    <div className="d-flex align-items-center mb-1" style={{ gap: 6 }}>
      <BmAvatar name={name} pictureUri={pictureUri} size={18} />
      <Typography variant="caption" color="textSecondary">
        {name ?? 'Unknown Business Manager'}
        {id && ` (id: ${id})`}
      </Typography>
    </div>
  )
}

const HealthChip = ({
  hasWarning,
  hasError,
  message,
}: {
  hasWarning: boolean
  hasError: boolean
  message?: string
}) => {
  if (hasError) {
    return (
      <Tooltip title={message ?? ''} disableHoverListener={!message}>
        <Chip
          size="small"
          icon={<WarningIcon style={{ color: severityColors.critical.text }} fontSize="small" />}
          label="Needs attention"
          style={{
            background: severityColors.critical.background,
            color: severityColors.critical.text,
          }}
        />
      </Tooltip>
    )
  }
  if (hasWarning) {
    return (
      <Tooltip title={message ?? ''} disableHoverListener={!message}>
        <Chip
          size="small"
          icon={<WarningIcon style={{ color: severityColors.warning.text }} fontSize="small" />}
          label="Warning"
          style={{
            background: severityColors.warning.background,
            color: severityColors.warning.text,
          }}
        />
      </Tooltip>
    )
  }
  return (
    <Chip
      size="small"
      icon={<CheckCircleIcon style={{ color: '#2e7d32' }} fontSize="small" />}
      label="Connected"
      style={{ background: '#eef7ee', color: '#2e7d32' }}
    />
  )
}

// Read-only by default once an asset is already assigned — changing it requires an explicit "Change"
// click, which swaps the row for the picker dropdown in place (same as the old always-editable
// behavior, just gated behind an explicit click now). Picking a new asset doesn't apply it directly —
// it brings up a confirm dialog first, showing the from/to change and the relevant warnings (like a
// file-move confirmation), and only calls assignFacebookAsset once confirmed. The one exception is the
// very first assignment (nothing to compare against): the picker renders immediately (no read-only
// state to gate) and the pick assigns right away with no confirm step, since there's no "previous
// asset" to warn about — this is what makes the creator flow share this exact component with no
// separate code path.
const FbAssignableField = (props: FbAssignableFieldProps) => {
  const {
    type,
    title,
    optional,
    tooltipText,
    accountID,
    currentAssetId,
    currentAssetName,
    isNewBusiness,
    initialOwnPictureUri,
    fbBusinessId,
    fbBusinessName,
    fbBusinessPictureUri,
    hasError,
    disabled,
    onAssigned,
    externalWarning,
    clearable,
    onCleared,
    onRefreshAssets,
    refreshingAssets,
    refreshToken,
    onRefreshComplete,
  } = props

  const [editing, setEditing] = useState(!currentAssetId)
  const [candidate, setCandidate] = useState<CommonFBBusinessObjectFields | undefined>()
  const [confirming, setConfirming] = useState(false)
  const [error, setError] = useState<string | undefined>()
  const [localWarning, setLocalWarning] = useState<string | undefined>()
  // Bumped on every Cancel (this field's own, or the change-confirmation dialog's) so
  // FacebookObjectSelector reverts any picked-but-never-confirmed value back to what's actually
  // assigned — without this, ad_account/page (kept mounted at all times) would keep showing the
  // abandoned pick, stuck in the selector view with no way back to read-only (DRI-203 follow-up).
  const [selectorResetToken, setSelectorResetToken] = useState(0)
  // Set once this field has been successfully (re)assigned this session, so the row can show it was
  // touched even after it settles back to a normal "Connected" health chip. Local-only — cleared on
  // next page load, same lifetime as the rest of this component's state.
  const [updatedThisSession, setUpdatedThisSession] = useState(false)
  // Instagram's own picture can't be derived from a guessable public URL like Page's can (see
  // pageOwnPictureUrl below), so it's seeded from initialOwnPictureUri (resolved server-side on
  // business load) and kept in sync with whatever the most recent in-session assign returned.
  const [assignedPicture, setAssignedPicture] = useState<string | undefined>(initialOwnPictureUri)
  // the caller can resolve the picture after mount too (e.g. the Instagram re-check on an Ad Account
  // change, or a Builder conversion), so follow it rather than only seeding from it
  useEffect(() => {
    if (initialOwnPictureUri) setAssignedPicture(initialOwnPictureUri)
  }, [initialOwnPictureUri])

  // Clearing (Page/Instagram only) unsets currentAssetId out from under an already-read-only row —
  // without this, the row stays stuck showing stale/blank content in its "connected" layout instead of
  // falling back to the picker. Only forces back to editing on the empty transition; doesn't fight the
  // user's own Change click by forcing editing false again once something is re-assigned (doAssign
  // already handles that).
  useEffect(() => {
    if (!currentAssetId) {
      setEditing(true)
      setLocalWarning(undefined)
      setUpdatedThisSession(false)
      setAssignedPicture(undefined)
    }
  }, [currentAssetId])

  const doAssign = async (assetId: string, assetName: string) => {
    setConfirming(true)
    setError(undefined)
    try {
      const result = await businessManagerService.assignFacebookAsset({
        field: FIELD_MAP[type],
        assetId,
        currentAdAccountId:
          type === 'pixel' || type === 'offline' || type === 'ig' ? accountID : undefined,
      })

      if (result.warning) {
        toast.warn(result.warning.message)
        setLocalWarning(result.warning.message)
      } else {
        setLocalWarning(undefined)
        // Reconnecting the same asset has no dialog to show success in — a plain "Connected" chip could
        // otherwise look like nothing happened, so confirm the retry actually worked.
        if (!candidate && (hasError || externalWarning)) toast.success('Access restored.')
      }

      onAssigned(result, assetId, assetName)
      setCandidate(undefined)
      setUpdatedThisSession(true)
      setAssignedPicture(result.picture)
      setEditing(false)
    } catch (e) {
      const message =
        e instanceof Error
          ? e.message
          : 'Failed to assign this asset. Check your access in Facebook.'
      setError(message)
      // Reconnect (re-assigning the same already-selected asset) has no dialog open to show `error`
      // inline in, unlike a picker-driven change — surface the failure as a toast so it's not silent.
      if (!candidate) toast.error(message)
    } finally {
      setConfirming(false)
    }
  }

  const handleSelect = (fbObject: CommonFBBusinessObjectFields | undefined) => {
    if (!fbObject) return

    // Nothing assigned yet, OR this business has never been saved at all (still being created) — in
    // both cases there's no real prior asset with actual history to warn about losing, so skip the
    // confirm dialog and assign immediately. Without the isNewBusiness check, picking a second Ad
    // Account before ever saving would show a "historical data won't merge" warning for data that was
    // never actually attributed to anything, since currentAssetId is already set from the first pick.
    if (!currentAssetId || isNewBusiness) {
      doAssign(fbObject.id, fbObject.name)
      return
    }

    setCandidate(fbObject)
  }

  const ownPictureUri =
    pageOwnPictureUrl(type, currentAssetId) ?? (type === 'ig' ? assignedPicture : undefined)
  const rowPictureUri = ownPictureUri ?? fbBusinessPictureUri
  const rowAvatarName = ownPictureUri ? currentAssetName ?? undefined : fbBusinessName

  // Ad Account/Page don't depend on any other field to list their options, so keep their selector
  // mounted at all times — hidden via CSS while read-only, never unmounted — so its fetch-on-mount
  // effect runs immediately on page load and its fetched list survives toggling Change on and off. If
  // it were only mounted while editing, switching back to read-only and then Change again would remount
  // it fresh and refetch, defeating the point of preloading. Dataset/Offline Dataset/Instagram all
  // depend on accountID, so preloading them ahead of the Ad Account pick wouldn't return anything useful
  // anyway; those keep the original mount-only-while-editing behavior.
  const preloadable = type === 'ad_account' || type === 'page'
  const showSelector = editing || preloadable

  // Matches FacebookObjectSelector's own internal `px-2` exactly, since that component (used for the
  // editing state below) isn't ours to restyle. Title and BM info are rendered exactly once, always,
  // above both states — FacebookObjectSelector is given an empty title (it skips rendering its own
  // title line entirely when empty, so there's no double title and no phantom spacing from an empty
  // title span) so our title/BmInfoHeader block is the single source of truth for both.
  return (
    <div className="d-flex flex-column w-100 py-2">
      <div className="px-2">
        <span className="pb-2 d-flex align-items-center" style={{ gap: 6 }}>
          {title}
          {optional && (
            <Chip
              size="small"
              label="Optional"
              variant="outlined"
              style={{ height: 18, fontSize: '0.7rem' }}
            />
          )}
          {tooltipText && (
            <span title={tooltipText} style={{ cursor: 'help', color: '#9e9e9e' }}>
              &#9432;
            </span>
          )}
        </span>
        <BmInfoHeader id={fbBusinessId} name={fbBusinessName} pictureUri={fbBusinessPictureUri} />
      </div>

      {!editing && (
        <div className="px-2">
          <div
            className="d-flex align-items-center flex-wrap border rounded"
            style={{ minHeight: 75, padding: '10px 12px', gap: 8 }}
          >
            <div
              className="d-flex align-items-center"
              style={{ gap: 8, minWidth: 0, flex: '1 1 200px' }}
            >
              <BmAvatar name={rowAvatarName} pictureUri={rowPictureUri} size={40} />
              <div style={{ minWidth: 0 }}>
                <Typography variant="body2" style={{ fontWeight: 600 }} noWrap>
                  {currentAssetName ?? currentAssetId}
                </Typography>
                <Typography variant="caption" color="textSecondary" noWrap>
                  id: {currentAssetId}
                </Typography>
              </div>
            </div>
            <div
              className="d-flex align-items-center flex-wrap"
              style={{ gap: 8, marginLeft: 'auto' }}
            >
              {updatedThisSession && (
                <Chip
                  size="small"
                  icon={<HistoryIcon fontSize="small" />}
                  label="Updated"
                  variant="outlined"
                />
              )}
              <HealthChip
                hasWarning={!!(externalWarning ?? localWarning)}
                hasError={hasError}
                message={externalWarning ?? localWarning}
              />
              {(hasError || externalWarning) && currentAssetId && (
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  onClick={() => doAssign(currentAssetId, currentAssetName ?? currentAssetId)}
                  disabled={disabled || confirming}
                >
                  {confirming ? 'Reconnecting...' : 'Reconnect'}
                </Button>
              )}
              <Button
                size="small"
                variant="outlined"
                onClick={() => setEditing(true)}
                disabled={disabled}
              >
                Change
              </Button>
              {clearable && (
                <Button size="small" onClick={onCleared} disabled={disabled}>
                  Clear
                </Button>
              )}
            </div>
          </div>
          {(externalWarning ?? localWarning) && (
            <p
              className="mb-0 mt-1"
              style={{ fontSize: '0.85rem', color: severityColors.warning.text }}
            >
              {externalWarning ?? localWarning}
            </p>
          )}
        </div>
      )}

      {showSelector && (
        <div style={editing ? undefined : { display: 'none' }} aria-hidden={!editing}>
          <FacebookObjectSelector
            title=""
            placeholder={`Pick a${/^[aeiou]/i.test(title) ? 'n' : ''} ${title}...`}
            disabled={disabled}
            type={type}
            accountID={accountID}
            selectedFbObjectID={currentAssetId}
            selectedFbObjectName={currentAssetName}
            selectedFbObjectPictureUrl={ownPictureUri ?? fbBusinessPictureUri}
            assignFbObject={fbObject => handleSelect(fbObject)}
            resetToken={selectorResetToken}
            assigning={confirming && !candidate}
            onRefreshAssets={onRefreshAssets}
            refreshingAssets={refreshingAssets}
            refreshToken={refreshToken}
            onRefreshComplete={onRefreshComplete}
          />
          {editing && currentAssetId && (
            <div className="px-2 pb-2">
              <Button
                size="small"
                onClick={() => {
                  setEditing(false)
                  setSelectorResetToken(t => t + 1)
                }}
              >
                Cancel
              </Button>
            </div>
          )}
        </div>
      )}

      {candidate && (
        <FbFieldChangeDialog
          open
          field={type}
          currentAsset={
            currentAssetId
              ? {
                  id: currentAssetId,
                  name: currentAssetName ?? '-',
                  businessId: fbBusinessId,
                  businessName: fbBusinessName,
                  businessPictureUri: fbBusinessPictureUri,
                }
              : undefined
          }
          candidateAsset={{
            id: candidate.id,
            name: candidate.name,
            businessId: candidate.business?.id,
            businessName: candidate.business?.name,
            businessPictureUri: candidate.business?.profile_picture_uri,
          }}
          confirming={confirming}
          error={error}
          onConfirm={() => doAssign(candidate.id, candidate.name)}
          onCancel={() => {
            setCandidate(undefined)
            setError(undefined)
            setEditing(false)
            setSelectorResetToken(t => t + 1)
          }}
        />
      )}
    </div>
  )
}

export default FbAssignableField
